// =============================================================================
// Guest session — persystencja credentiali konta-gościa (tryb gościa)
// =============================================================================
// Konto-gość powstaje automatycznie przy pierwszym wejściu (`POST /auth/guest`).
// Cookie JWT (30 dni) trzyma sesję, ale gdy wygaśnie / zostanie wyczyszczone,
// klient musi móc się ZALOGOWAĆ ponownie na to samo konto — dlatego trzymamy
// `username` + losowe `password` w localStorage.
//
// Bezpieczeństwo: to hasło konta ANONIMOWEGO (losowe, nieużywane nigdzie
// indziej, niskiej wartości — gość bez prawdziwego loginu). localStorage jest
// tu akceptowalne — to w praktyce trwały token sesji, jak dawny zapis IDB.
// =============================================================================

const STORAGE_KEY = 'capibara.guest';

export interface GuestCreds {
  username: string;
  password: string;
}

/** Odczyt credentiali gościa z localStorage. `null` gdy brak / niedostępne. */
export function loadGuestCreds(): GuestCreds | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GuestCreds>;
    if (typeof parsed.username === 'string' && typeof parsed.password === 'string') {
      return { username: parsed.username, password: parsed.password };
    }
    return null;
  } catch {
    // localStorage niedostępny (incognito / disabled) lub corrupt JSON.
    return null;
  }
}

/** Zapis credentiali gościa. Błąd (quota / disabled) — tylko warn, nie rzuca. */
export function saveGuestCreds(creds: GuestCreds): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(creds));
  } catch (err) {
    console.warn('[guestSession] Nie udało się zapisać credentiali gościa:', err);
  }
}

/** Usuwa zapisane credentiale gościa (np. po nieaktualnym koncie). */
export function clearGuestCreds(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore — i tak nie ma czego czyścić
  }
}

/**
 * Generuje losowe hasło konta-gościa — 48 znaków hex (24 bajty entropii).
 * Spełnia serwerową regułę długości (min 8) z gigantycznym zapasem.
 */
export function makeGuestPassword(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
